import { hashKey, type QueryClient, type QueryKey } from '@tanstack/react-query';

interface OptimisticRollbackOwner {
  readonly token: object;
  readonly value: unknown;
}

const activeOwners = new WeakMap<QueryClient, Map<string, OptimisticRollbackOwner>>();

export function readOptimisticRollbackToken(
  queryClient: QueryClient,
  queryKey: QueryKey,
): object | undefined {
  return activeOwners.get(queryClient)?.get(hashKey(queryKey))?.token;
}

export function claimOptimisticRollback(
  queryClient: QueryClient,
  queryKey: QueryKey,
  token: object,
  value: unknown,
): void {
  ownerMap(queryClient).set(hashKey(queryKey), { token, value });
}

export function ownsOptimisticRollback(
  queryClient: QueryClient,
  queryKey: QueryKey,
  token: object,
  value: unknown,
): boolean {
  const owner = activeOwners.get(queryClient)?.get(hashKey(queryKey));
  return owner?.token === token && owner.value === value;
}

export function restoreOptimisticRollback(
  queryClient: QueryClient,
  queryKey: QueryKey,
  token: object | undefined,
  value: unknown,
): void {
  if (token === undefined) {
    supersedeOptimisticRollback(queryClient, queryKey);
    return;
  }
  claimOptimisticRollback(queryClient, queryKey, token, value);
}

export function releaseOptimisticRollback(
  queryClient: QueryClient,
  queryKey: QueryKey,
  token: object,
): void {
  const owners = activeOwners.get(queryClient);
  const key = hashKey(queryKey);
  if (owners?.get(key)?.token !== token) return;
  owners.delete(key);
  if (owners.size === 0) activeOwners.delete(queryClient);
}

export function supersedeOptimisticRollback(queryClient: QueryClient, queryKey: QueryKey): void {
  const owners = activeOwners.get(queryClient);
  owners?.delete(hashKey(queryKey));
  if (owners?.size === 0) activeOwners.delete(queryClient);
}

function ownerMap(queryClient: QueryClient): Map<string, OptimisticRollbackOwner> {
  let owners = activeOwners.get(queryClient);
  if (owners === undefined) {
    owners = new Map();
    activeOwners.set(queryClient, owners);
  }
  return owners;
}
